import { useGetQueuedPlaysQuery } from '@carboncopyco/data';
import { DownOutlineIcon } from '@carboncopyco/icons';
import { BigPinkPlayButtonIllustration } from '@carboncopyco/illustrations';
import {
  Button,
  EmptyView,
  Paper,
  Separator,
  TextBase,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { QueuedPlayRow } from './QueuedPlayRow';

export const PresentationQueue = () => {
  const { organizationId } = useParams();
  const [isOverflowing, setIsOverflowing] = useBoolean();

  const [isExpanded, setIsExpanded] = useBoolean();
  const scrollContainerRef = useRef<HTMLDivElement>();
  const [listBottomRef, _, entry] = useInView({
    root: scrollContainerRef.current,
  });
  useEffect(() => {
    setIsOverflowing.set(!entry?.isIntersecting);
  }, [entry, setIsOverflowing]);

  const {
    data: { items: plays = [] } = {},
    isLoading,
  } = useGetQueuedPlaysQuery({
    organizationId,
    limit: 30,
  });

  const isEmpty = !isLoading && !plays.length;

  return (
    <Paper
      radius
      border="ds-regular"
      fillParent
    >
      <div className="flex flex-col w-full h-full">
        <div className="flex flex-col grow overflow-hidden">
          <div className="p-5 flex gap-1.5">
            <TextLargeParagraph
              label="Upcoming demos"
              color="text-ds-natural-600"
            />
          </div>
          <Separator horizontal />
          {isEmpty ? (
            <div className="flex-1 flex items-center">
              <EmptyView
                noPadding
                noBg
                illustration={<BigPinkPlayButtonIllustration />}
                title="No upcoming demos"
                message={
                  <div>
                    <TextBase
                      label="Your saved demos would appear here"
                      color="text-ds-natural-300"
                    />
                  </div>
                }
              />
            </div>
          ) : (
            <>
              <div
                ref={scrollContainerRef}
                className={classNames(
                  'w-full relative',
                  isExpanded ? 'overflow-auto' : 'overflow-hidden'
                )}
              >
                {plays.map((play) => (
                  <QueuedPlayRow
                    key={play.id}
                    play={play}
                  />
                ))}
                <div ref={listBottomRef} />
              </div>
            </>
          )}
        </div>

        {isOverflowing && !isExpanded && !isEmpty && (
          <div className="pt-3 pb-2">
            <Button
              link
              blue
              label="Show more"
              rightIcon={<DownOutlineIcon />}
              onClick={setIsExpanded.setTrue}
            />
          </div>
        )}
      </div>
    </Paper>
  );
};
